/**
 * The "Items to review" tab pattern (TMMAPP-0159 §F 27), written once for every page with a to-do
 * block: **Items to review · N** is the first tab, then the page's main tab, then its other tabs.
 *
 * - With no `?tab=`, the page opens on Items to review when its count is above zero, else on the main
 *   tab; a `?tab=` the person chose (or a link sent) always wins.
 * - No flash: until the count is known the strip shows with nothing chosen and the panel is a loading
 *   state, and the default is picked once, when the count arrives. It is never re-picked: when the last
 *   item is resolved the person stays, with "All caught up" and a button to the main tab.
 * - The count is the length of the list the tab already serves (or a served total), reported by the
 *   tab's own content; it's never a read of its own.
 *
 * The review content stays mounted while another tab shows (hidden), so it reads once and reports its
 * count; the page's other panels render only while chosen, as before. The hooks that pick the tab are
 * in `reviewTab.ts`.
 */
import type { ReactNode } from 'react';
import { Box, Button, Skeleton, Tab, Tabs, type SxProps, type Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EmptyCard } from './EmptyCard';
import { REVIEW_TAB, type ReviewCount, type ReviewTabValue } from './reviewTab';

export interface ReviewTabItem<T extends string> {
  value: T;
  label: string;
}

interface ReviewTabsProps<T extends string> {
  /** The shown tab; `null` while the default waits for the count. */
  value: T | ReviewTabValue | null;
  count: ReviewCount;
  /** False when the page has nothing to review here: no review tab. */
  enabled?: boolean;
  /** The main tab first, then the page's other tabs in their order. */
  tabs: ReviewTabItem<T>[];
  onChange: (next: T | ReviewTabValue) => void;
  /** The strip's accessible name. */
  label: string;
  /** The review panel's id, for `aria-controls`. */
  panelId?: string;
  sx?: SxProps<Theme>;
}

export function ReviewTabs<T extends string>({
  value,
  count,
  enabled = true,
  tabs,
  onChange,
  label,
  panelId,
  sx,
}: ReviewTabsProps<T>) {
  const { t } = useTranslation();
  return (
    <Tabs
      value={value ?? false}
      onChange={(_event, next: T | ReviewTabValue) => onChange(next)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      aria-label={label}
      aria-busy={value === null ? true : undefined}
      data-review-tabs=""
      {...(sx !== undefined ? { sx } : {})}
    >
      {enabled && (
        <Tab
          value={REVIEW_TAB}
          // "Items to review · 3", or "Items to review" while the count is unknown.
          label={
            typeof count === 'number'
              ? t('reviewTabs.labelCount', { count })
              : t('reviewTabs.label')
          }
          {...(panelId !== undefined ? { id: `${panelId}-tab`, 'aria-controls': panelId } : {})}
        />
      )}
      {tabs.map((tab) => (
        <Tab key={tab.value} value={tab.value} label={tab.label} />
      ))}
    </Tabs>
  );
}

/** The panel area while the default waits for the count: a loading state, never a tab's content. */
export function ReviewTabLoading() {
  const { t } = useTranslation();
  return (
    <Skeleton
      variant="rounded"
      height={160}
      role="status"
      aria-label={t('common.loading')}
      data-review-loading=""
    />
  );
}

interface ReviewPanelProps {
  /** Shown (else mounted but hidden, so its content still reads and reports its count). */
  active: boolean;
  count: ReviewCount;
  /** The main tab's name, for "All caught up"'s button. */
  mainLabel: string;
  onMain: () => void;
  /** Must match `ReviewTabs`' `panelId`. */
  id: string;
  children: ReactNode;
}

/** The Items to review panel. At zero it says "All caught up", with a button to the main tab. */
export function ReviewPanel({ active, count, mainLabel, onMain, id, children }: ReviewPanelProps) {
  const { t } = useTranslation();
  return (
    <Box
      role="tabpanel"
      id={id}
      aria-labelledby={`${id}-tab`}
      hidden={!active}
      data-review-panel=""
      sx={{ display: active ? 'flex' : 'none', flexDirection: 'column', gap: 2 }}
    >
      {count === 0 && (
        <EmptyCard
          action={
            <Button variant="outlined" onClick={onMain}>
              {t('reviewTabs.toMain', { tab: mainLabel })}
            </Button>
          }
        >
          <Box component="strong" sx={{ display: 'block', color: 'text.primary' }}>
            {t('reviewTabs.caughtUp')}
          </Box>
          {t('reviewTabs.caughtUpBody')}
        </EmptyCard>
      )}
      {children}
    </Box>
  );
}
